import React, { useState, useRef, useEffect } from "react";
import { FileUpload } from "@/components/FileUpload";
import { DynoChart } from "@/components/DynoChart";
import { DataTable } from "@/components/DataTable";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Moon, Sun } from "lucide-react";
import { UnitsModal } from "@/components/ui/units-modal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTheme } from "@/components/theme-provider";
import { parseDynoData, type DataPoint, type ParsedData } from "@/lib/csv-parser";
import { Features } from "@/components/Features";
import { EmptyChart } from "@/components/EmptyChart";
import { SupportedFormats } from "@/components/LogoWatermark";
import { ChartControls } from "@/components/ChartControls";
import Footer from "@/components/Footer";

const Index = () => {
  const [data, setData] = useState<DataPoint[]>([]);
  const [maxRow, setMaxRow] = useState<DataPoint | null>(null);
  const [result, setResult] = useState<ParsedData | null>(null);
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  const [measurement, setMeasurement] = useState<"metric" | "imperial">("metric");
  const [isUnitsModalOpen, setIsUnitsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTheme("light");
  }, [setTheme]);

  useEffect(() => {
    const handleMeasurementChange = (event: CustomEvent) => {
      setMeasurement(event.detail);
    };

    window.addEventListener('measurementChanged', handleMeasurementChange as EventListener);
    return () => {
      window.removeEventListener('measurementChanged', handleMeasurementChange as EventListener);
    };
  }, []);

  const handleFileSelect = async (file: File) => {
    try {
      const newResult = await parseDynoData(file, {
        measurement,
        toast,
        minAccelPercent: 75
      });
      setResult(newResult);
      setData(newResult.data);
      setMaxRow(newResult.maxRow);
    } catch (error) {
      console.error('Failed to parse CSV:', error);
    }
  };

  useEffect(() => {
    if (data.length > 0) {
      handleFileSelect(data[0] as unknown as File).catch(console.error);
    }
  }, [measurement]);

  const exportAsPNG = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current);
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "dyno-chart.png";
      link.href = url;
      link.click();
      toast({
        title: "Success",
        description: "Chart exported as PNG"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export chart",
        variant: "destructive"
      });
    }
  };

  const exportAsPDF = async () => {
    if (!chartRef.current) return;
    try {
      const canvas = await html2canvas(chartRef.current);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("dyno-chart.pdf");
      toast({
        title: "Success",
        description: "Chart exported as PDF"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export chart",
        variant: "destructive"
      });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'DynoGraph Pro Chart',
        text: 'Check out my dyno results!',
        url: window.location.href
      }).catch(console.error);
    } else {
      toast({
        title: "Error",
        description: "Sharing is not supported on this device",
        variant: "destructive"
      });
    }
  };

  const handleClear = () => {
    setData([]);
    setMaxRow(null);
    setResult(null);
    toast({
      title: "Success",
      description: "All data cleared"
    });
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted p-8">
        <div className="mx-auto max-w-6xl space-y-8">
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <h1 className="text-4xl font-bold tracking-tight flex items-center justify-center gap-4">
                <img 
                  src="/dyno.svg" 
                  alt="DynoGraph Pro Logo" 
                  className="w-12 h-12 dark:invert transition-transform hover:scale-110" 
                  style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))' }}
                />
                DynoGraph Pro
              </h1>
              <p className="mt-2 text-lg text-muted-foreground">
                Professional-grade dyno data visualization and analysis
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
          </div>

          <Features />

          <div className="space-y-6">
            <FileUpload onFileSelect={handleFileSelect} />
            <SupportedFormats />
          </div>

          <div className="space-y-4">
            <ChartControls 
              onFullscreen={() => {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                  setIsFullscreen(false);
                } else {
                  chartRef.current?.requestFullscreen();
                  setIsFullscreen(true);
                }
              }}
              onShare={handleShare}
              onPrint={() => window.print()}
              onExportPNG={exportAsPNG}
              onExportPDF={exportAsPDF}
              onOpenUnits={() => setIsUnitsModalOpen(true)}
              onClear={handleClear}
              isFullscreen={isFullscreen}
            />

            <div ref={chartRef}>
              {data.length > 0 ? <DynoChart data={data} measurement={measurement} /> : <EmptyChart />}
            </div>

            {data.length > 0 && result && (
              <Card className="w-full p-4 bg-background">
                <div className="grid grid-cols-4 gap-4">
                  <div className="flex flex-col items-center justify-center p-2 text-center">
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Gear</h3>
                    <Separator className="mb-2" />
                    <p className="text-lg font-bold">{maxRow ? maxRow.gear.toString() : "N/A"}</p>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2 text-center">
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">RPM Range</h3>
                    <Separator className="mb-2" />
                    <p className="text-lg font-bold">
                      {maxRow ? `${result.rpmRange.min.toLocaleString()} - ${result.rpmRange.max.toLocaleString()}` : "N/A"}
                    </p>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2 text-center">
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Peak Torque</h3>
                    <Separator className="mb-2" />
                    <p className="text-lg font-bold">{maxRow ? `${maxRow.torque.toFixed(2)} Nm` : "N/A"}</p>
                  </div>
                  <div className="flex flex-col items-center justify-center p-2 text-center">
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Peak Horsepower</h3>
                    <Separator className="mb-2" />
                    <p className="text-lg font-bold">{maxRow ? `${result.peakHorsepower.toFixed(2)} hp` : "0"}</p>
                  </div>
                </div>
              </Card>
            )}
          </div>

          <UnitsModal 
            open={isUnitsModalOpen} 
            onOpenChange={setIsUnitsModalOpen}
            onSave={(settings) => {
              setMeasurement(settings.measurementSystem);
              toast({
                title: "Success",
                description: "Units settings updated"
              });
            }}
          />

          {data.length > 0 && <DataTable data={data} measurement={measurement} />}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Index;
